import React, { useMemo, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { formatCurrency, formatDate } from '../i18n';
import { useToast } from '../contexts/ToastContext';
import { useOrgFeatureFlag } from '../hooks/useOrgFeatureFlags';
import {
  ConfirmDialog,
  DataTableShell,
  EmptyState,
  InlineMessage,
  ResponsiveRows,
  StatusChip,
} from './ui';
import type { ResponsiveRowsColumn } from './ui';
import {
  PRICE_CHANGE_REVIEW_ITEMS_KEY,
  PriceChangeReviewItem,
  PriceChangeSnapshot,
  approveAllPriceChanges,
  decidePriceChange,
  fetchPriceChangeReviewItems,
} from '../services/priceChangeReviewApi';

const SOON_MS = 48 * 60 * 60 * 1000;

/** How many changes are waiting, for the tab badge. Null while the flag is off. */
export function usePendingPriceChangeCount(): number | null {
  const { t } = useAppTranslation();
  const { enabled } = useOrgFeatureFlag('price_change_review');
  const { data } = useQuery({
    queryKey: [...PRICE_CHANGE_REVIEW_ITEMS_KEY, 'pending'],
    queryFn: () => fetchPriceChangeReviewItems('pending', t('store.priceChangeReview.pending.loadError')),
    enabled,
    staleTime: 30_000,
  });
  return enabled ? data?.pendingCount ?? 0 : null;
}

const COLUMNS: ResponsiveRowsColumn[] = [
  { key: 'product', header: '', width: 'minmax(0,2fr)' },
  { key: 'source', header: '', width: 'minmax(0,1.2fr)', mobileLabel: true },
  { key: 'change', header: '', width: 'minmax(0,1.6fr)', mobileLabel: true },
  { key: 'effective', header: '', width: 'minmax(0,1fr)', mobileLabel: true },
  { key: 'actions', header: '', width: '200px', align: 'end' },
];

/**
 * MAP Pricing Data, Pending changes (flag `price_change_review`; Estimate row
 * B38, #490): the price and rebate changes a store's approval step is holding,
 * with approve, decline, approve all for a brand and approve all. An owner or
 * admin decides; the server refuses anyone else and the toast says so.
 */
export const PendingPriceChangesTab: React.FC = () => {
  const { t } = useAppTranslation();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [confirm, setConfirm] = useState<{ brand: string | null; count: number } | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: [...PRICE_CHANGE_REVIEW_ITEMS_KEY, 'pending'],
    queryFn: () => fetchPriceChangeReviewItems('pending', t('store.priceChangeReview.pending.loadError')),
  });
  const items = useMemo(() => data?.items ?? [], [data]);
  const brands = useMemo(
    () => Array.from(new Set(items.map((i) => i.brand).filter((b): b is string => !!b))).sort((a, b) => a.localeCompare(b)),
    [items],
  );

  const refresh = () => queryClient.invalidateQueries({ queryKey: PRICE_CHANGE_REVIEW_ITEMS_KEY });

  const decide = useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: 'approve' | 'decline' }) =>
      decidePriceChange(id, decision, t('store.priceChangeReview.pending.actionError')),
    onSuccess: (_result, { decision }) => {
      showToast(t(decision === 'approve' ? 'store.priceChangeReview.pending.approved' : 'store.priceChangeReview.pending.declined'), 'success');
      refresh();
    },
    onError: (error: Error) => showToast(error.message, 'error'),
  });

  const approveAll = useMutation({
    mutationFn: (brand: string | null) => approveAllPriceChanges(brand, t('store.priceChangeReview.pending.actionError')),
    onSuccess: (count) => {
      showToast(t('store.priceChangeReview.pending.approvedMany', { count }), 'success');
      setConfirm(null);
      refresh();
    },
    onError: (error: Error) => {
      showToast(error.message, 'error');
      setConfirm(null);
    },
  });

  const money = (value: number | null) => (value == null ? '-' : formatCurrency(value));
  const snapshot = (s: PriceChangeSnapshot, showCompareAt: boolean) => (
    <Stack spacing={0}>
      <Typography variant="body2">{money(s.price)}</Typography>
      {showCompareAt && (
        <Typography variant="caption" color="text.secondary">
          {s.compareAt ? t('store.priceChangeReview.pending.compareAt', { amount: formatCurrency(s.compareAt) }) : t('store.priceChangeReview.pending.noCompareAt')}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary">
        {s.instantRebate ? t('store.priceChangeReview.pending.rebate', { amount: formatCurrency(s.instantRebate) }) : t('store.priceChangeReview.pending.noRebate')}
      </Typography>
    </Stack>
  );

  const renderCell = (row: PriceChangeReviewItem, key: string): React.ReactNode => {
    switch (key) {
      case 'product':
        return (
          <Stack spacing={0}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.name || row.model || row.sku || row.productId}</Typography>
            <Typography variant="caption" color="text.secondary">
              {[row.brand, row.sku, row.integrationName].filter(Boolean).join(' · ')}
            </Typography>
          </Stack>
        );
      case 'source':
        return <StatusChip tone="info" label={t(`store.priceChangeReview.pending.source_${row.source}`)} />;
      case 'change': {
        // A compare-at-only change would otherwise read as before == after.
        const showCompareAt = !!(row.before.compareAt || row.after.compareAt);
        return (
          <Stack direction="row" spacing={1} alignItems="center">
            {snapshot(row.before, showCompareAt)}
            <Typography variant="body2" color="text.secondary" aria-hidden>{'->'}</Typography>
            {snapshot(row.after, showCompareAt)}
          </Stack>
        );
      }
      case 'effective': {
        if (!row.effectiveAt) return <Typography variant="body2">{t('store.priceChangeReview.pending.noDate')}</Typography>;
        const at = new Date(row.effectiveAt);
        const soon = at.getTime() - Date.now() <= SOON_MS;
        return (
          <Stack spacing={0.5} alignItems="flex-start">
            <Typography variant="body2">{formatDate(at)}</Typography>
            {soon && <StatusChip tone="warning" label={t('store.priceChangeReview.pending.soon')} />}
          </Stack>
        );
      }
      default:
        return (
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button size="small" variant="contained" disabled={decide.isPending} onClick={() => decide.mutate({ id: row.id, decision: 'approve' })}>
              {t('store.priceChangeReview.pending.approve')}
            </Button>
            <Button size="small" variant="outlined" disabled={decide.isPending} onClick={() => decide.mutate({ id: row.id, decision: 'decline' })}>
              {t('store.priceChangeReview.pending.decline')}
            </Button>
          </Stack>
        );
    }
  };

  const HEADERS: Record<string, string> = {
    product: t('store.priceChangeReview.pending.product'),
    source: t('store.priceChangeReview.pending.source'),
    change: t('store.priceChangeReview.pending.change'),
    effective: t('store.priceChangeReview.pending.effective'),
    actions: t('store.priceChangeReview.pending.actions'),
  };
  const columns = COLUMNS.map((column) => ({ ...column, header: HEADERS[column.key] }));

  if (isError) return <InlineMessage severity="error">{t('store.priceChangeReview.pending.loadError')}</InlineMessage>;

  const toolbar = (
    <Stack spacing={1.5}>
      <Typography variant="body2" color="text.secondary">{t('store.priceChangeReview.pending.intro')}</Typography>
      {items.length > 0 && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
          <Button size="small" variant="contained" onClick={() => setConfirm({ brand: null, count: data?.pendingCount ?? items.length })}>
            {t('store.priceChangeReview.pending.approveAll')}
          </Button>
          {brands.map((brand) => (
            <Button
              key={brand}
              size="small"
              variant="outlined"
              onClick={() => setConfirm({ brand, count: items.filter((i) => i.brand === brand).length })}
            >
              {t('store.priceChangeReview.pending.approveBrand', { brand })}
            </Button>
          ))}
        </Stack>
      )}
    </Stack>
  );

  return (
    <Box>
      <DataTableShell toolbar={toolbar}>
        <ResponsiveRows
          columns={columns}
          rows={items}
          getRowKey={(row) => row.id}
          renderCell={renderCell}
          isLoading={isLoading}
          emptyState={
            <EmptyState
              size="compact"
              title={t('store.priceChangeReview.pending.empty')}
              description={t('store.priceChangeReview.pending.emptyDescription')}
            />
          }
        />
      </DataTableShell>
      <ConfirmDialog
        open={confirm !== null}
        title={t('store.priceChangeReview.pending.confirmTitle')}
        message={
          confirm?.brand
            ? t('store.priceChangeReview.pending.confirmBrand', { count: confirm.count, brand: confirm.brand })
            : t('store.priceChangeReview.pending.confirmAll', { count: confirm?.count ?? 0 })
        }
        confirmLabel={t('store.priceChangeReview.pending.approve')}
        cancelLabel={t('common.cancel')}
        loading={approveAll.isPending}
        onConfirm={() => approveAll.mutate(confirm?.brand ?? null)}
        onClose={() => setConfirm(null)}
      />
    </Box>
  );
};

export default PendingPriceChangesTab;
